<template>
  <component
    :is="containerComponent"
    v-bind="containerProps"
    v-on="containerListeners"
    class="schedule-gaps-container"
    :class="{ 'schedule-gaps-container--embedded': embedded }"
  >
    <div class="schedule-gaps-modal" :class="{ 'schedule-gaps-modal--embedded': embedded }">
      <h3 v-if="embedded" class="schedule-gaps-modal__title">Available Regular Schedule Gaps</h3>

      <p v-if="!embedded" class="schedule-gaps-modal__description">
        Choose a position to see current regular schedule gaps before sending an invite.
      </p>

      <label v-if="!embedded" class="schedule-gaps-modal__field">
        <span>Position</span>
        <custom-select
          :value.sync="selectedRoleName"
          :options="resolvedQualifications"
          :loading="loadingOptions"
          name="name"
          optionKey="name"
          placeholder="Select position"
          @change="handlePositionChange"
        />
      </label>

      <label class="schedule-gaps-modal__field" v-if="effective_date">
        <span
          >Effective Date: <b>{{ effective_date }}</b></span
        >
      </label>

      <a-spin :spinning="loading" tip="Loading available regular schedule gaps…">
        <div v-if="error" class="schedule-gaps-modal__state schedule-gaps-modal__state--error">
          Regular schedule gaps could not be loaded. Try again or contact support if the issue continues.
        </div>

        <div v-else-if="!roleFilteredGaps.length" class="schedule-gaps-modal__state">
          {{ emptyStateText }}
        </div>

        <div v-else class="schedule-gaps-modal__results">
          <div class="schedule-gaps-modal__day-nav">
            <a-button
              v-for="option in dayOptions"
              :key="option.key"
              size="small"
              :type="selectedDayKey === option.key ? 'primary' : 'default'"
              @click="selectedDayKey = option.key"
            >
              {{ option.label }}
            </a-button>
          </div>

          <div class="schedule-gaps-modal__summary">{{ resultsSummary }}</div>

          <div class="schedule-gaps-modal__scroll">
            <div v-for="group in groupedGaps" :key="group.key" class="schedule-gaps-modal__group">
              <h4 class="schedule-gaps-modal__group-title">{{ group.title }}</h4>

              <div class="schedule-gaps-modal__gap-list">
                <div v-for="gap in group.items" :key="getGapKey(gap)" class="schedule-gaps-modal__gap">
                  <div v-if="!activeRoleName">
                    <span class="schedule-gaps-modal__label">Position</span>
                    <strong>{{ gap.position_name || '-' }}</strong>
                  </div>
                  <div>
                    <span class="schedule-gaps-modal__label">Time</span>
                    <strong>{{ formatTime(gap) }}</strong>
                  </div>
                  <div>
                    <span class="schedule-gaps-modal__label">Floor</span>
                    <strong>{{ gap.location_name || '-' }}</strong>
                  </div>
                  <div>
                    <span class="schedule-gaps-modal__label">Available spots</span>
                    <strong>{{ gap.needed_count ?? '-' }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-spin>
    </div>

    <template v-if="!embedded" #footer><a-button @click="handleClose">Close</a-button></template>
  </component>
</template>

<script>
import { Button, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import CustomSelect from '@/components/ui/custom-select.vue'
import util from '@/util'

const { mapActions } = createNamespacedHelpers('user')

export default {
  components: {
    'a-button': Button,
    'a-spin': Spin,
    'custom-select': CustomSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    embedded: {
      type: Boolean,
      default: false
    },
    roleName: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      selectedRoleName: undefined,
      resolvedQualifications: [],
      gaps: [],
      effective_date: null,
      selectedDayKey: 'all',
      loading: false,
      loadingOptions: false,
      error: false
    }
  },
  computed: {
    containerComponent() {
      return this.embedded ? 'section' : 'modal-fullscreen'
    },

    containerProps() {
      if (this.embedded) {
        return {}
      }

      return {
        visible: this.visible,
        title: 'Available Regular Schedule Gaps',
        width: '780px'
      }
    },

    containerListeners() {
      return this.embedded ? {} : { cancel: this.handleClose }
    },

    activeRoleName() {
      return this.embedded ? this.roleName : this.selectedRoleName
    },

    resultsSummary() {
      if (this.activeRoleName) {
        return `${this.filteredGaps.length} available gaps for ${this.activeRoleName}`
      }

      return `${this.filteredGaps.length} available gaps`
    },

    emptyStateText() {
      if (this.activeRoleName) {
        return 'No available regular schedule gaps for this role.'
      }

      return 'No available regular schedule gaps.'
    },

    dayOptions() {
      const options = this.roleFilteredGaps.reduce(
        (result, gap) => {
          const key = this.getDayKey(gap)

          if (!result.map[key]) {
            result.map[key] = true
            result.items.push({
              key,
              label: this.formatDayNavigationLabel(gap)
            })
          }

          return result
        },
        {
          map: {},
          items: [{ key: 'all', label: 'All' }]
        }
      )

      return options.items
    },

    filteredGaps() {
      if (this.selectedDayKey === 'all') {
        return this.roleFilteredGaps
      }

      return this.roleFilteredGaps.filter(gap => this.getDayKey(gap) === this.selectedDayKey)
    },

    roleFilteredGaps() {
      if (!this.activeRoleName) {
        return this.gaps
      }

      const hasResolvedRole = this.resolvedQualifications.some(role => role.name === this.activeRoleName)

      if (!hasResolvedRole) {
        return []
      }

      return this.gaps.filter(gap => gap.position_name === this.activeRoleName)
    },

    groupedGaps() {
      const groups = this.filteredGaps.reduce((result, gap) => {
        const key = this.getDayKey(gap)

        if (!result[key]) {
          result[key] = {
            key,
            title: this.formatDay(gap),
            items: []
          }
        }

        result[key].items.push(gap)

        return result
      }, {})

      return Object.values(groups)
    }
  },
  watch: {
    visible: {
      async handler(value) {
        if (value) {
          this.selectedRoleName = undefined
          this.gaps = []
          this.effective_date = null
          this.selectedDayKey = 'all'
          this.error = false
          await this.fetchGaps()
        }
      },
      immediate: true
    },
    roleName() {
      this.selectedDayKey = 'all'
    }
  },
  methods: {
    ...mapActions(['getAssociateScheduleGaps']),

    async fetchGaps() {
      this.loading = true
      this.loadingOptions = true

      try {
        const data = await this.getAssociateScheduleGaps()
        this.resolvedQualifications = data?.resolved_qualifications ?? []
        this.gaps = data?.gaps ?? []
        this.effective_date = data?.min_date
      } catch (error) {
        this.error = true
      } finally {
        this.loading = false
        this.loadingOptions = false
      }
    },

    handlePositionChange() {
      this.selectedDayKey = 'all'
    },

    handleClose() {
      this.$emit('update:visible', false)
    },

    formatDay(gap) {
      if (gap.date) {
        return util.convertDateTimeToCurrentTimeZoneWithFormat(gap.date, 'dddd, MM/DD/YYYY')
      }

      return gap.weekday || '-'
    },

    formatDayNavigationLabel(gap) {
      if (gap.date) {
        return util.convertDateTimeToCurrentTimeZoneWithFormat(gap.date, 'ddd MM/DD')
      }

      return gap.weekday || '-'
    },

    getDayKey(gap) {
      return gap.date || gap.weekday || 'unknown'
    },

    formatTime(gap) {
      const start = gap.local_time_range?.start
      const end = gap.local_time_range?.end
      const shiftName = gap.shift_name ? `${gap.shift_name}: ` : ''

      if (!start || !end) {
        return shiftName ? shiftName.slice(0, -2) : '-'
      }

      return `${shiftName}${start} - ${end}`
    },

    getGapKey(gap) {
      return [
        gap.position_id,
        gap.date,
        gap.shift_definition_id,
        gap.location_id,
        gap.half,
        gap.local_time_range?.start,
        gap.local_time_range?.end
      ].join('-')
    }
  }
}
</script>

<style scoped>
.schedule-gaps-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.schedule-gaps-container--embedded,
.schedule-gaps-modal--embedded {
  min-height: 0;
  height: 100%;
}

.schedule-gaps-modal__title {
  margin: 0;
  color: var(--heading-color);
  font-size: 18px;
}

.schedule-gaps-modal__description {
  margin: 0;
  color: var(--text-color-alpha6);
}

.schedule-gaps-modal__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--text-color-alpha8);
  font-weight: 600;
}

.schedule-gaps-modal__field ::v-deep .ant-select {
  width: 100%;
}

.schedule-gaps-modal__state {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 120px;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 1px dashed var(--border-color-base);
  border-radius: var(--border-radius);
  color: var(--text-color-alpha6);
  text-align: center;
}

.schedule-gaps-modal__state--error {
  border-color: var(--error-color);
  color: var(--error-color);
}

.schedule-gaps-modal__results {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  overflow: hidden;
}

.schedule-gaps-modal__summary {
  flex: 0 0 auto;
  color: var(--heading-color);
  font-weight: 700;
}

.schedule-gaps-modal__day-nav {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.schedule-gaps-modal__day-nav .ant-btn {
  flex: 0 0 auto;
}

.schedule-gaps-modal__scroll {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  overflow: auto;
  max-height: 500px;
  padding-right: 4px;
}

.schedule-gaps-modal__group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.schedule-gaps-modal__group-title {
  margin: 0;
  color: var(--text-color-alpha8);
  font-size: 14px;
  font-weight: 700;
}

.schedule-gaps-modal__gap-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 8px;
}

.schedule-gaps-modal__gap {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
  padding: 12px;
  border: 1px solid var(--border-color-base);
  border-radius: var(--border-radius);
  background-color: var(--bg-component);
}

.schedule-gaps-modal__gap > div {
  min-width: 0;
}

.schedule-gaps-modal__gap strong {
  display: block;
  overflow: hidden;
  color: var(--text-color);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.schedule-gaps-modal__label {
  display: block;
  margin-bottom: 2px;
  color: var(--text-color-alpha6);
  font-size: 12px;
}

@media (max-width: 640px) {
  .schedule-gaps-modal__gap {
    grid-template-columns: 1fr;
  }
}
</style>
